import React, {
  FormEvent,
  LegacyRef,
  useEffect,
  useRef,
  useState,
} from "react";
import * as Ariakit from "@ariakit/react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AxiosError } from "axios";
import { BE_BASE_URL } from "../../../utils/config";
import { Color } from "../../../constants/color";
import { axiosInstance, useAuth } from "../../../provider/authProvider";
import { selectUser, storeUser } from "../../../features/user/userSlice";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { goToPage } from "../../../utils/utils";
import { revalidateToken } from "../../../features/user/utils";
import {
  dimenticaAziendaDiSessione,
  ricordaAziendaDiSessione,
} from "../../../features/user/aziendaDiSessione";
import { UserType } from "../../../types";
import Label from "../../label";
import Textbox from "../input/index";
import SubmitButton from "../submit";
import { Select, SelectItem, SelectProvider } from "../../form/select";

const FormWrapper = styled.form<{
  $error: boolean;
}>`
  order: 2;
  display: flex;
  flex-direction: column;
  row-gap: 1rem;
  position: relative;
  min-width: 25rem;
  min-height: 22rem;
  height: ${(props) => (props.$error ? "24rem" : "unset")};
  max-height: 25rem;
  margin: 3.2rem auto auto;
  border-radius: 2rem 2rem 2rem 2rem;
  background-color: ${Color.WHITE};
  box-shadow: 0 0 8px 4px #e2e2e2;
  button {
    margin: auto;
  }
`;

const Header = styled.h1`
  background-color: ${Color.LIGHT_BLUE};
  border-radius: 2rem 2rem 0 0;
  color: ${Color.WHITE};
  text-align: center;
  line-height: 80px;
  font-size: 20px;
  font-family: "Avenir Next Bold", sans-serif;
  margin: 2px 2px 0 2px;
`;

const ErrorMessage = styled.div<{
  $error: boolean;
}>`
  display: ${(props) => (props.$error ? "unset" : "none")};
  text-align: center;
  max-width: 20rem;
  height: auto;
  min-height: 2.5rem;
  padding: 0.5rem;
  margin-top: 0.6rem;
  margin-left: 2.5rem;
  background-color: ${Color.PINK};
  border-radius: 0.6rem;
  border-style: solid;
  border-width: 2px;
  border-color: ${Color.RED};
  div {
    margin-top: 0.25rem;
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${Color.WHITE};
  background-color: ${Color.LIGHT_BLUE};
  padding: 0.8em 2em;
  border-radius: 18px;
  border-style: hidden;
  font-size: 1.2rem;
  font-weight: bold;
  cursor: pointer;
  align-self: center;
  &:disabled {
    background-color: ${Color.LIGHT_GREY};
    cursor: not-allowed;
  }
`;

interface LoginFormProps {
  onSuperAdminStatusChange?: (isSuperAdmin: boolean) => void;
}

const LoginForm = ({ onSuperAdminStatusChange }: LoginFormProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userStore = useAppSelector(selectUser);
  const { token, setToken } = useAuth();
  const formRef: LegacyRef<HTMLFormElement> | undefined = React.createRef();

  const [error, setError] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isSuperAdmim, setIsSuperAdmin] = useState(false);
  const [aziendaOptions, setAziendaOptions] = useState<Map<string, string>>(
    new Map()
  );
  const [azienda, setAzienda] = useState<string>("");
  const [aziendaLabel, setAziendaLabel] = useState<string>("");

  const loginDataRef = useRef<UserType>();
  const tokenRef = useRef<string>();
  const errorMsgRef = useRef<string | null>();

  useEffect(() => {
    //console.log("useEffect loginForm", { token, userStore });
    // Se esiste un token E l'utente non è in first login, reindirizza alla dashboard
    if (token && !userStore.isFirstLogin) {
      if (userStore.firstName.length === 0) {
        /*
         Alla dashboard solo se il token vale ancora. Prima ci si andava comunque, anche con la
         verifica fallita: con un cookie sopravvissuto al suo token la dashboard prendeva 401
         ovunque e il login rimandava li', senza via d'uscita. Se non vale lo si butta - e con
         lui il cookie - e si resta qui a rifare l'accesso. Solo con un 401, pero': se il server
         non risponde il token puo' essere ancora buono, e si resta qui senza buttarlo.
        */
        revalidateToken(setToken, dispatch).then((status) => {
          if (status === 200) {
            goToPage("dashboard");
          } else if (status === 401) {
            setToken("logout");
          }
        });
      } else {
        goToPage("dashboard");
      }
    }
  }, [
    dispatch,
    setToken,
    token,
    userStore,
    userStore.firstName,
    userStore.firstName.length,
  ]);

  const aziendaStore = Ariakit.useSelectStore({
    value: aziendaLabel, // Show the label in the dropdown
    setValue: (label) => {
      setAziendaLabel(label);
      // Find the ID corresponding to the label selected
      const selectedAziendaId =
        Array.from(aziendaOptions.entries()).find(
          ([, value]) => value === label
        )?.[0] || "";
      setAzienda(selectedAziendaId);
    },
  });

  /*
   Si entra solo dall'invio del modulo: Invio in un campo lo invia gia' da se'. Fino al
   2026-09-28 i due campi chiamavano login anche dal keyup di Invio, e ogni Invio mandava due
   POST /auth/signin - con il limite sui tentativi falliti, una password sbagliata ne consumava
   due.
  */
  const login = async (e: FormEvent<HTMLFormElement> | KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type !== "submit" && e.type !== "keyup") {
      return;
    }
    try {
      const response = await axiosInstance.post(BE_BASE_URL + "/auth/signin", {
        username: username,
        password: password,
      });

      if (response.status == 200) {
        setError(false);
        errorMsgRef.current = null;
        const loginData = response.data;
        //console.log("loginData", { loginData });
        if (loginData.isFirstLogin) {
          dispatch(
            storeUser({
              username: loginData.username,
              email: loginData.email,
              id: loginData.id,
              firstName: loginData.firstName,
              lastName: loginData.lastName,
              role: loginData.role,
              phone: loginData.phone,
              isFirstLogin: loginData.isFirstLogin,
              solaLettura: loginData.solaLettura === true,
            })
          );
          setToken(loginData.token);
          goToPage(`login/firstLogin`);
          return; // Impedisce l'esecuzione del resto della funzione
        }
        if (loginData.companies) {
          loginDataRef.current = {
            username: loginData.username,
            email: loginData.email,
            id: loginData.id,
            firstName: loginData.firstName,
            lastName: loginData.lastName,
            role: loginData.role,
            phone: loginData.phone,
            isFirstLogin: loginData.isFirstLogin,
          };
          tokenRef.current = loginData.token;
          //console.log("Mostrare la scelta delle società al super admin");
          const data: { id: string; businessName: string }[] =
            loginData.companies;
          const optionsMap = new Map(
            data.map(({ id, businessName }) => [id, businessName])
          );
          setAziendaOptions(optionsMap);
          setIsSuperAdmin(true);
          if (onSuperAdminStatusChange) onSuperAdminStatusChange(true);
        } else {
          // Chi vede una sola azienda non sceglie: la scheda non ha un'azienda propria da
          // ricordare, e un residuo di un accesso precedente sarebbe peggio del nulla.
          dimenticaAziendaDiSessione();
          dispatch(
            storeUser({
              username: loginData.username,
              email: loginData.email,
              id: loginData.id,
              firstName: loginData.firstName,
              lastName: loginData.lastName,
              role: loginData.role,
              phone: loginData.phone,
              isFirstLogin: loginData.isFirstLogin,
              solaLettura: loginData.solaLettura === true,
            })
          );
          setToken(loginData.token);
        }
      }
    } catch (e: unknown) {
      const err = e as any;
      setError(true);

      if (err.response && err.response.data) {
        // Gestisci la risposta di errore dal server
        if (typeof err.response.data === "string") {
          errorMsgRef.current = err.response.data;
        } else if (
          typeof err.response.data === "object" &&
          err.response.data.message
        ) {
          errorMsgRef.current = err.response.data.message;
        } else {
          errorMsgRef.current = "Errore di autenticazione";
        }
      } else {
        errorMsgRef.current = "Errore imprevisto, riprovare";
      }
    }
  };

  const handleSelezionaAzienda = async (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (azienda) {
      try {
        const url = `${BE_BASE_URL}/auth/signin/${azienda}`;
        // Devo aggiungere manualmente header authorization con il token
        // perchè non ho ancora messo il token nello store
        const response = await axiosInstance.post(url, null, {
          headers: {
            Authorization: `Bearer ${tokenRef.current}`,
          },
        });
        if (
          response.status == 200 &&
          loginDataRef.current &&
          tokenRef.current
        ) {
          const scelta = response.data;
          /*
           Da qui in avanti vale il token della scelta, non quello del login: l'azienda di una
           richiesta vive nel token (claim `companyId`, ADR-0017 del backend), e quello del login
           porta ancora l'azienda di partenza. Tenerlo significherebbe non cambiare azienda.

           Se il backend non restituisce un token nuovo - la versione precedente lasciava quel
           campo vuoto - si resta su quello del login invece di azzerare la sessione: la scelta
           non ha effetto, come prima, ma nessuno viene buttato fuori dall'applicazione.
          */
          // La scelta resta in questa scheda: e' cio' che la rende indipendente dalle altre.
          ricordaAziendaDiSessione({ id: azienda, businessName: aziendaLabel });
          dispatch(
            storeUser({
              username: scelta.username,
              email: scelta.email,
              id: scelta.id,
              firstName: scelta.firstName,
              lastName: scelta.lastName,
              role: scelta.role,
              phone: scelta.phone,
              isFirstLogin: scelta.isFirstLogin,
              solaLettura: scelta.solaLettura === true,
            })
          );
          setToken(scelta.token || tokenRef.current);

          setError(false);
          errorMsgRef.current = null;
        }
      } catch (e: unknown) {
        const err = e as AxiosError;
        if (err.response && err.response.data && err.message) {
          // console.log("error if", err);
          setError(true);
          errorMsgRef.current = "Errore imprevisto, ricaricare la pagina";
          setIsSuperAdmin(false);
          setAzienda("");
          setAziendaLabel("");
        } else {
          // console.log("error else", err);
          // TODO Handle generic error
          setError(true);
          errorMsgRef.current = "Errore imprevisto, riprovare";
        }
      }
    }
  };

  return (
    <FormWrapper id={"login"} ref={formRef} onSubmit={login} $error={error}>
      <Header>{t("login-header")}</Header>
      {error && (
        <ErrorMessage $error={error}>
          <Label
            text={errorMsgRef.current || t("login-error")}
            color={Color.RED}
            fontSize={"13px"}
          />
        </ErrorMessage>
      )}
      {isSuperAdmim ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "5rem",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              paddingInline: "1rem",
            }}
          >
            <label
              htmlFor="azienda"
              style={{
                fontFamily: "Avenir Next, sans-serif",
                fontWeight: "bold",
                color: Color.LIGHT_BLUE,
                textAlign: "center",
                maxWidth: "350px",
              }}
            >
              Come amministratore devi selezionare un'azienda per proseguire
            </label>
            <SelectProvider store={aziendaStore}>
              <Select id="azienda">
                <SelectItem key="" value="" disabled>
                  Seleziona un'azienda
                </SelectItem>
                {/*
                 In ordine alfabetico: l'elenco arriva nell'ordine del backend, e chi
                 amministra sette aziende le cerca per nome.
                */}
                {Array.from(aziendaOptions.entries())
                  .sort(([, a], [, b]) => a.localeCompare(b))
                  .map(([key, value]) => (
                    <SelectItem key={`azienda_${key}`} value={value}>
                      {value}
                    </SelectItem>
                  ))}
              </Select>
            </SelectProvider>
          </div>
          <Button
            name={"seleziona_azienda"}
            disabled={azienda.length === 0}
            style={{
              padding: "0.5rem 1.5rem",
            }}
            onClick={handleSelezionaAzienda}
          >
            Seleziona
          </Button>
        </div>
      ) : (
        <>
          <Textbox
            label={t("login-username")}
            alt={"username"}
            form={"login"}
            autocomplete={"username"}
            name={"username"}
            placeholder={t("login-username-placeholder")}
            type={"text"}
            onChange={(e) => setUsername(e.target.value)}
          />
          <Textbox
            label={t("login-password")}
            alt={"password"}
            form={"login"}
            autocomplete={"current-password"}
            name={"password"}
            placeholder={t("login-password-placeholder")}
            type={"password"}
            onChange={(e) => setPassword(e.target.value)}
          />
          <SubmitButton
            label={t("login-submit")}
            form={"login"}
            name={"submit login"}
            style={{
              padding: "0.5rem 1.5rem",
            }}
          />
        </>
      )}
    </FormWrapper>
  );
};

export default LoginForm;
